import React, {
	useCallback,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import {
	Card,
	Flex,
	Input,
	Modal as AntdModal,
	Popconfirm,
	Space,
	Tabs,
	Tag,
	Tooltip,
} from 'antd';
import { useHistory } from 'react-router';
import {
	CompassOutlined,
	CopyOutlined,
	DeleteOutlined,
	EditOutlined,
	LockOutlined,
} from '@ant-design/icons';
import classNames from 'classnames';
import { useQueryClient } from 'react-query';
import {
	CargoContractSides,
	CargoContractStatusTypes,
	EstimateStatus,
	EstimatorModeTypes,
	FixtureTypes,
} from '@shared/utils/constants';
import { sortByDates } from '@shared/utils/sortByDates';
import { PortRotationEntryGeneratorTypes } from '@shared/utils/port-rotation-entry-generator-types';
import { asyncForEach } from '@shared/utils/array';
import Button from '@client/components/Button';
import Table from '@client/components/Table/Table';
import {
	createContractFromEstimate,
	deleteFixture,
	updateCargo,
	updateEstimateGroup,
} from '@client/lib/api';
import showErrorNotification from '@client/utils/showErrorNotification';
import showSuccessNotification from '@client/utils/showSuccessNotification';
import { Links } from '@client/utils/links';
import { useSaveButton } from '@client/utils/hooks/useSaveButton';
import Modal from '@client/components/Modal/Modal';
import { useEstimate } from '../context/EstimatorContext';
import { useCentralStore } from '../context/CentralStoreContext';
import { useEstimateChangesProvider } from '../context/EstimateChangesContext';
import { useGridChanges } from '../context/GridChangesContext';
import { getMissingFixData } from '../helpers/getMissingFixData';
import styles from './styles/TopBar.module.css';
import ImportFromCargoBookModal from './ImportFromCargoBookModal';
import CargoContractStatusTag from './CargoContractStatusTag';
import KeyboardShortcutsButton from './KeyboardShortcutsButton';

const TopBar = ({ mode }: { mode: EstimatorModeTypes }) => {
	const [bunkerPricesModalOpen, setBunkerPricesModalOpen] = useState(false);
	const [editingId, setEditingId] = useState<number | null>(null);
	const [estimateName, setEstimateName] = useState<string | null>(null);
	const [showImportModal, setShowImportModal] = useState(false);

	const history = useHistory();
	const queryClient = useQueryClient();

	const {
		estimate,
		onUpdateEstimate,
		fixtureHasInvoices,
		fixtureHasDisSyncedDAs,
	} = useEstimate();

	const {
		fixtureDetails,
		cargos,
		estimates,
		selectedEstimateId,
		setSelectedEstimateId,
		handleCreateSubEstimate,
		onDeleteEstimate,
		loading,
		refreshEverything,
		isGroupFixed,
	} = useCentralStore();

	const {
		saving,
		onSaveChanges,
		immediateSaveChanges,
		hasPendingChanges,
	} = useEstimateChangesProvider();

	const {
		hasPendingGridChanges,
		savingGrid,
		onSaveGridChanges,
	} = useGridChanges();

	const switchTab = async (estimateId: number) => {
		if (hasPendingChanges) {
			await onSaveChanges();
		}

		setSelectedEstimateId(estimateId);
	};

	const isVcReadyForFix = () => {
		let ready = true;
		const messages = [];

		// Since we can create the fix, without any cargo entries
		if (!cargos) {
			ready = true;

			return { ready, messages: [] };
		}

		if (!estimate) {
			ready = false;
			messages.push('Estimate is missing.');
		}

		const vcOutCargo = cargos.find(
			(cargo) => cargo.contractSide === CargoContractSides.VC_OUT,
		);

		const hasMissingPorts = cargos.some((cargo) => {
			const loadingPorts = cargo.contractSide === CargoContractSides.VC_IN ?
				vcOutCargo?.loadingPorts :
				cargo.loadingPorts;

			const dischargePorts = cargo.contractSide === CargoContractSides.VC_IN ?
				vcOutCargo?.dischargePorts :
				cargo.dischargePorts;

			return !loadingPorts?.length || !dischargePorts?.length;
		});

		const hasMissingQuantity = cargos.some((cargo) => {
			const quantity = cargo.contractSide === CargoContractSides.VC_IN ?
				vcOutCargo?.quantity :
				cargo.quantity;

			return !quantity;
		});

		if (cargos.some((cargo) => !cargo.charterer)) {
			ready = false;
			messages.push('Some cargos are missing a counterparty.');
		}

		if (cargos.some((cargo) => cargo.fixingCompanyId == null)) {
			ready = false;
			messages.push('Some cargos are missing a Fixing Company.');
		} else if (new Set(cargos.map((cargo) => cargo.fixingCompanyId)).size > 1) {
			ready = false;
			messages.push('All cargos must have the same Fixing Company.');
		}

		if (hasMissingPorts) {
			ready = false;
			messages.push('Some cargos are missing loading or discharging ports.');
		}

		if (hasMissingQuantity) {
			ready = false;
			messages.push('Some cargos are missing a quantity.');
		}

		return { ready, messages };
	};

	const isTcReadyForFix = () => {
		let ready = true;
		const messages: string[] = [];

		if (estimate == null || fixtureDetails == null) {
			return {
				ready: false,
				messages: [],
			};
		}

		const portRotation = estimate.portRotationEntries;
		const ballastEntry = portRotation.find((e) => (
			e.generatedBy === PortRotationEntryGeneratorTypes.BALLAST_LEG
		));

		const deliveryEntry = portRotation.find((e) => (
			e.generatedBy === PortRotationEntryGeneratorTypes.DELIVERY_PORT
		));

		const redeliveryEntry = portRotation.find((e) => (
			e.generatedBy === PortRotationEntryGeneratorTypes.REDELIVERY_PORT
		));

		if (fixtureDetails.periodSchedule.length === 0) {
			ready = false;
			messages.push('Period schedule is missing');
		}

		if (fixtureDetails.periodSchedule.some((e) => e.hireRate == null)) {
			ready = false;
			messages.push('Period schedule must have a hire rate');
		}

		if (fixtureDetails.counterpartyId == null) {
			ready = false;
			messages.push('Charterer is missing');
		}

		if (fixtureDetails.fixingCompanyId == null) {
			ready = false;
			messages.push('Fixing Company is missing');
		}

		if (fixtureDetails.vesselId == null || estimate.vessel?.id == null) {
			ready = false;
			messages.push('Vessel is missing');
		}

		if (ballastEntry == null || ballastEntry.port == null) {
			ready = false;
			messages.push('Specify ballast port');
		}

		if (deliveryEntry == null || deliveryEntry.port == null) {
			ready = false;
			messages.push('Specify delivery port');
		}

		if (redeliveryEntry == null || redeliveryEntry.port == null) {
			ready = false;
			messages.push('Specify redelivery port');
		}

		if (fixtureDetails.bankAccountId == null) {
			ready = false;
			messages.push('Select a bank account in TC Terms');
		}

		return { ready, messages };
	};

	const isReadyForFix = () => {
		if (estimate == null) {
			return {
				ready: false,
				message: null,
			};
		}

		const { ready, messages } = estimate.type === FixtureTypes.SPOT ?
			isVcReadyForFix() :
			isTcReadyForFix();

		const messageJSX = (
			<ul>
				{messages.map((msg, index) => (
					<li key={index}>{msg}</li>
				))}
			</ul>
		);

		return {
			ready,
			message: messageJSX,
		};
	};

	const {
		ready: isReadyToFix,
		message: isReadyToFixMessage,
	} = isReadyForFix();

	const currentEstimateHasPendingCargo = useMemo(() => (
		cargos?.some((cargo) => (
			cargo.contractStatus === CargoContractStatusTypes.Pending
		)) ?? false
	), [cargos]);

	const otherEstimateHasTentativeOrContractedCargo = useMemo(() => (
		estimates?.some((e) => (
			e.id !== estimate?.id &&
			e.Cargos?.some((cargo) => (
				cargo.contractStatus === CargoContractStatusTypes.TentativeBooking ||
				cargo.contractStatus === CargoContractStatusTypes.Contracted
			))
		)) ?? false
	), [estimates, estimate?.id]);

	const shouldWarnAboutOtherEstimateCargos =
		currentEstimateHasPendingCargo &&
		otherEstimateHasTentativeOrContractedCargo;

	const createContract = async () => {
		if (estimate == null) {
			return;
		}

		try {
			const id = await createContractFromEstimate(estimate.id);

			showSuccessNotification('Contract created');
			history.push(Links.Voyage.get(id));

			await queryClient.invalidateQueries({
				queryKey: ['estimateGroups'],
				exact: false,
			});
			await queryClient.refetchQueries(['estimateGroups']);
		} catch (e) {
			showErrorNotification('Could not create contract', e as Error);
		}
	};

	const confirmFixWithOtherEstimateCargos = async () => {
		if (!shouldWarnAboutOtherEstimateCargos) {
			await createContract();

			return;
		}

		AntdModal.confirm({
			title: 'Fix this estimate?',
			content: (
				<Flex vertical gap={6}>
					<div>
						Estimate(s) in this group of estimates has cargoes with status:
					</div>
					<Flex align="center" gap={10}>
						<CargoContractStatusTag
							status={CargoContractStatusTypes.TentativeBooking}
						/>
						<span>or</span>
						<CargoContractStatusTag
							status={CargoContractStatusTypes.Contracted}
						/>
					</Flex>
					<div>
						This estimate doesn&apos;t.
					</div>
					<div>
						Are you sure you want to fix this estimate?
					</div>
				</Flex>
			),
			okText: 'Yes',
			cancelText: 'Cancel',
			onOk: async () => {
				await createContract();
			},
		});
	};

	const fixEstimate = async () => {
		const missingFixData = estimate?.type === FixtureTypes.SPOT ?
			getMissingFixData({ estimate, cargos: cargos ?? [] }) :
			[];

		if (missingFixData.length === 0) {
			await confirmFixWithOtherEstimateCargos();

			return;
		}

		AntdModal.confirm({
			title: 'Some fields are not filled in on the estimate',
			content: (
				<ul data-testid="missing-fix-data">
					{missingFixData.map((item) => (
						<li key={item}>{item}</li>
					))}
				</ul>
			),
			okText: 'Fix anyway',
			cancelText: 'Cancel',
			onOk: confirmFixWithOtherEstimateCargos,
		});
	};

	const saveName = useCallback(async () => {
		if (editingId == null || !estimates) {
			return;
		}

		onUpdateEstimate({ name: estimateName });

		if (editingId === estimates[0]?.id) {
			await updateEstimateGroup({
				groupId: estimates[0].estimateGroupId,
				groupName: estimateName,
			});
		}

		await refreshEverything();
		setEditingId(null);
	}, [editingId, estimates, onUpdateEstimate, estimateName, refreshEverything]);

	const isReadyToUnfix = useMemo(() => {
		let ready = true;
		const messages = [];

		if (fixtureHasInvoices) {
			ready = false;
			messages.push('An estimate cannot be fixed, if the associated contract has an invoice raised');
		}

		const messageJSX = (
			<ul>
				{messages.map((msg, index) => (
					<li key={index}>{msg}</li>
				))}
			</ul>
		);

		return {
			ready,
			message: messageJSX,
		};
	}, [fixtureHasInvoices]);

	const performUnfixEstimate = useCallback(async () => {
		if (estimate?.fixtureId == null) {
			return;
		}

		const totalEstimateCost = estimate.EstimateCosts?.reduce((sum, cost) => {
			const amount = cost.amount ?? 0;
			const rate = cost.exchangeRate ?? 1;

			return sum + amount / rate;
		}, 0);

		await immediateSaveChanges(
			async () => {
				onUpdateEstimate({
					status: EstimateStatus.Available,
					miscCostTotal: totalEstimateCost,
					fixtureId: null,
				});
				await asyncForEach(
					estimate?.cargos ?? [],
					async (c) => {
						await updateCargo({
							cargoId: c.id,
							attributes: {
								contractStatus: CargoContractStatusTypes.Contracted,
							},
						});
					},
				);
				await refreshEverything();
				await queryClient.invalidateQueries();
			},
			async () => {
				if (estimate.type === FixtureTypes.SPOT) {
					await deleteFixture(estimate.fixtureId!);
				}
			},
		);
	}, [estimate?.fixtureId, estimate?.EstimateCosts, immediateSaveChanges, onUpdateEstimate]);

	const onUnfixEstimate = useCallback(async () => {
		if (estimate?.fixtureId == null) {
			return;
		}

		if (fixtureHasInvoices) {
			showErrorNotification(
				'Could not unfix estimate',
				new Error('An estimate cannot be unfixed, if the associated contract has an invoice raised'),
			);

			return;
		}

		if (fixtureHasDisSyncedDAs) {
			AntdModal.confirm({
				title: 'Unfix estimate?',
				content:
					'This contract has disbursement accounts synced from DA-Desk. Unfixing will cancel the ' +
					'port call and those disbursement accounts in DA-Desk. Continue?',
				okText: 'Unfix anyway',
				cancelText: 'Cancel',
				okButtonProps: { danger: true },
				onOk: () => performUnfixEstimate(),
			});

			return;
		}

		await performUnfixEstimate();
	}, [
		estimate?.fixtureId,
		fixtureHasInvoices,
		fixtureHasDisSyncedDAs,
		performUnfixEstimate,
	]);

	const isFixed = useMemo(
		() => !(estimate?.fixtureId == null || !estimate.fixtureId),
		[estimate],
	);

	const tabsHeaderRef = useRef<HTMLDivElement | null>(null);
	const [fillTabs, setFillTabs] = useState(false);

	const items = sortByDates(estimates ?? [], 'createdAt').map((e) => {
		const isEditing = editingId === e.id;
		const isEstimateFixed = e.status === EstimateStatus.Fixed;

		const deleteDisabled = isEstimateFixed;
		const deleteTooltip = deleteDisabled ? 'Cannot delete fixed estimates' : 'Delete estimate';
		const tabHasRelet = e.Cargos?.some(
			(cargo) => cargo.contractSide === CargoContractSides.VC_IN
		);

		return {
			key: e.id.toString(),
			label: (
				<div style={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
					{isEditing ? (
						<>
							<Input
								size="small"
								width={120}
								value={estimateName ?? undefined}
								onChange={(evt) => setEstimateName(evt.target.value)}
								onKeyDown={(event) => event.stopPropagation()}
								onPressEnter={saveName}
							/>
						</>
					) : (
						<>
							<div
								style={{
									display: 'inline-flex',
									alignItems: 'center',
									gap: 6,
								}}
							>
								{tabHasRelet && (
									<Tag
										color='purple'
										style={{ cursor: 'pointer', marginRight: 0 }}
									>
										RELET
									</Tag>
								)}
								<span>
									{e.name == null ?
										`(${e.readableId})` :
										`(${e.readableId}) ${e.name}`}
								</span>
							</div>
							<div style={{ marginLeft: 6, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
								<span>
									<Button
										type="text"
										size="small"
										icon={isEstimateFixed ? <LockOutlined /> : <EditOutlined />}
										onClick={(evt) => {
											if (isEstimateFixed) {
												return;
											}

											evt?.stopPropagation();
											switchTab(e.id);
											setEditingId(e.id);
											setEstimateName(e.name);
										}}
										style={{
											padding: 0,
											minWidth: 'auto',
											height: 'auto',
											lineHeight: 1,
											color: '#1677ff',
										}}
									/>
								</span>
								<Tooltip title='Copy estimate'>
									<span>
										<Button
											type="text"
											size="small"
											icon={<CopyOutlined />}
											onClick={(evt) => {
												evt?.stopPropagation();
												handleCreateSubEstimate();
											}}
											style={{
												padding: 0,
												minWidth: 'auto',
												height: 'auto',
												lineHeight: 1,
												color: '#1677ff',
											}}
										/>
									</span>
								</Tooltip>
								<Tooltip title={deleteTooltip}>
									<span>
										<Popconfirm
											title="Delete estimate?"
											description="Are you sure you want to delete this estimate?"
											okText="Delete"
											cancelText="Cancel"
											okButtonProps={{ danger: true }}
											disabled={deleteDisabled}
											onConfirm={(evt) => {
												evt?.stopPropagation();
												onDeleteEstimate(e.id);
											}}
										>
											<Button
												type="text"
												size="small"
												disabled={deleteDisabled}
												danger
												icon={<DeleteOutlined />}
												onClick={(evt) => {
													evt?.stopPropagation();
												}}
												style={{
													padding: 0,
													minWidth: 'auto',
													height: 'auto',
													lineHeight: 1,
												}}
											/>
										</Popconfirm>
									</span>
								</Tooltip>
							</div>
						</>
					)}
				</div >
			),
		};
	});

	// Kunne ikke gøre dette med ren CSS da der sker noget creazy flickering
	// Ved ikke om dette er overkill, men det sikrer at tabs altid bliver tjekket 
	// for overflow og forhindrer crazy flickering!
	useLayoutEffect(() => {
		const tabs = tabsHeaderRef.current;

		if (!tabs) {
			return;
		}

		const checkTabs = () => {
			const wrap = tabs.querySelector<HTMLElement>('.ant-tabs-nav-wrap');
			const list = tabs.querySelector<HTMLElement>('.ant-tabs-nav-list');

			if (!wrap || !list) {
				return;
			}

			setFillTabs(list.scrollWidth <= wrap.clientWidth);
		};

		checkTabs();

		const resiseObserver = new ResizeObserver(checkTabs);
		resiseObserver.observe(tabs);

		const mutationObserver = new MutationObserver(checkTabs);
		mutationObserver.observe(tabs, { childList: true, subtree: true, characterData: true });

		return () => {
			resiseObserver.disconnect();
			mutationObserver.disconnect();
		};
	}, [items.length]);

	const isGridMode = mode === EstimatorModeTypes.GRID;

	const saveButton = useSaveButton({
		onSave: isGridMode ? onSaveGridChanges : onSaveChanges,
		title: 'Save & Calculate',
		loading: isGridMode ? savingGrid : saving || loading,
		hasPendingChanges: isGridMode ? hasPendingGridChanges : hasPendingChanges,
		testId: 'save-btn',
	});

	return (
		<>
			<Card className={styles.card}>
				<Flex justify="space-between" align="center">
					<div
						className={classNames(
							styles.tabContainer,
							{ [styles.grid]: mode === EstimatorModeTypes.GRID },
						)}
					>
						{mode !== EstimatorModeTypes.GRID && (
							<div ref={tabsHeaderRef}>
								<Tabs
									className={classNames(styles.tabs, { [styles.fillTabs]: fillTabs })}
									items={items}
									onTabClick={(key) => switchTab(Number(key))}
									activeKey={selectedEstimateId?.toString()}
									animated={false}
								/>
							</div>
						)}
					</div>
					<Space>
						<KeyboardShortcutsButton />
						{isFixed && estimate?.fixtureId != null && (
							<Button
								type="link"
								onClick={() => {
									if (estimate.voyage != null) {
										history.push(Links.Voyage.get(estimate?.voyage.id));
									}
								}}
								icon={(<CompassOutlined />)}
							>
								Go to contract
							</Button>
						)}
						{mode === EstimatorModeTypes.GRID && (
							<>
								<Button
									disabled={hasPendingGridChanges}
									onClick={() => setShowImportModal(true)}
									disabledTooltip="Save your changes first"
								>
									New estimate from cargo-book
								</Button>
								<Button
									disabled={hasPendingGridChanges}
									onClick={() => handleCreateSubEstimate({ blank: true })}
									disabledTooltip="Save your changes first"
								>
									Create blank estimate
								</Button>
								{saveButton}
							</>
						)}
						{mode !== EstimatorModeTypes.GRID && (
							<Space>
								{isFixed ? (
									<Button
										disabled={!isReadyToUnfix.ready}
										disabledTooltip={isReadyToUnfix.message}
										onClick={onUnfixEstimate}
									>
										Unfix Estimate
									</Button>
								) : (
									<Space>
										<Button
											disabled={!isReadyToFix || loading || isGroupFixed}
											disabledTooltip={
												isGroupFixed ?
													'An estimate in this group is already fixed' :
													isReadyToFixMessage
											}
											onClick={fixEstimate}
											type="primary"
										>
											Create Contract
										</Button>
										{saveButton}
									</Space>
								)}
							</Space>
						)}
					</Space>
				</Flex>
			</Card>
			<Modal
				open={bunkerPricesModalOpen}
				onCancel={() => setBunkerPricesModalOpen(false)}
			>
				<Table
					size="small"
					columns={[
						{ title: 'Port', dataIndex: 'port' },
						{ title: 'Grade', dataIndex: 'grade' },
						{ title: 'Price', dataIndex: 'price' },
					]}
					showSearch
					dataSource={[]}
				/>
			</Modal>
			<ImportFromCargoBookModal
				copyIntoBlankEstimate
				open={showImportModal}
				onClose={() => setShowImportModal(false)}
			/>
		</>
	);
};

export default TopBar;
